import { createSelector } from 'reselect';
import { get, sortBy, deburr } from 'lodash';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';

import { addressLine } from '../helpers/Formatters';
import { IVouchers } from '@epi-models/containers/OrderAmount';
import { UserCompany } from '@epi-models/pages/CompanyInfo';
import { IAddress } from '@epi-models/shared';

interface FormattedAddress {
  id: string;
  value: string;
  label: string;
}

interface Country {
  FinnishName: string;
  EnglishName: string;
  Name?: string;
  [key: string]: any;
}

export const getVouchers = state => state.apiVouchers.vouchers;

export const getContacts = state => state.apiContacts.contacts;

export const getOrderFees = state => state.apiOrderFees;

export const getAddresses = (state): IAddress[] =>
  state.apiAddresses.addresses || [];

export const getApiPayments = state => state.apiPayments;

export const getApiPaymentStatus = state => state.apiPaymentStatus;

export const isNewCustomer = state => state.apiVoucherOrder.isNewCustomer;

export const getApiErrors = state => state.apiErrors;

export const getApiPaymentsMethods = state => state.apiPaymentMethods;

export const getPaymentMethods = state =>
  state.apiPaymentMethods.paymentMethods;

export const getAvailableVouchersByType = (type: string) =>
  createSelector(getVouchers, (vouchers: IVouchers[] | null) =>
    (vouchers || []).filter(
      voucher =>
        voucher.type.toLowerCase() === type.toLowerCase() && voucher.available
    )
  );

export const isCompanyLoaded = (state): boolean =>
  !!(
    (state.apiCompanies.userCompany &&
      state.apiCompanies.userCompany.address &&
      state.apiCompanies.userCompany.address.addressLine) ||
    state.apiCompanies.isLoaded
  );

export const isProcessingCompanyUpdateSelector = (state): boolean =>
  state.apiCompanies.isProcessingUpdate;

export const getUserCompany = createSelector(
  state => get(state, 'apiCompanies.userCompany'),
  (userCompany): UserCompany => {
    return userCompany as UserCompany;
  }
);

export const hasEinvoiceInfo = createSelector(
  state => state.apiCompanies.userCompany,
  (userCompany): boolean =>
    !!(
      (userCompany as UserCompany)?.invoiceInformation?.eInvoiceAddress &&
      (userCompany as UserCompany)?.invoiceInformation?.operator
    )
);

export const getUserCompanyPaymentMethod = createSelector(
  state => state.apiCompanies.userCompany,
  (userCompany): string =>
    (userCompany as UserCompany)?.invoiceInformation?.invoiceMethod || ''
);

export const getCustomerNumber = (state): string | undefined =>
  state.apiCompanies.userCompany.customerNumber;

const formatAddress = (adr: IAddress): FormattedAddress => ({
  id: adr.id,
  value: adr.id,
  label: addressLine(adr)
});

export const getFormattedAddresses = createSelector(
  getAddresses,
  (addresses: IAddress[]) => addresses.map(formatAddress)
);

export const getFinnishAddresses = createSelector(
  getAddresses,
  (addresses: IAddress[]) =>
    addresses?.length
      ? addresses.filter(adr => adr.country === 'FI').map(formatAddress)
      : null
);

export const isLoadingInvoice =
  (idType: string, id: string) =>
  (state): boolean =>
    (state.apiDownloadInvoice.isLoading || []).includes(`${idType}-${id}`);

export const isLoadingReceiptForDeposit =
  (id: string) =>
  (state): boolean => {
    const array = state.apiDownloadReceiptForDepositOrder.isLoading || [];
    return array.includes(`depositOrder-${id}`);
  };

export const isLoadingReceiptForTransaction =
  (id: string) =>
  (state): boolean => {
    const array = state.apiDownloadReceiptForTransaction.isLoading || [];
    return array.includes(`transaction-${id}`);
  };

export const getTranslatedCountries = state => {
  const finnish =
    (getApplicationLanguageId(state) || '').toLowerCase() === 'fi';

  const countries = (state?.apiCountries?.countries || []).map(
    (item: Country) => ({
      ...item,
      Name: finnish ? item.FinnishName : item.EnglishName
    })
  );
  const sortedCountries = sortBy(countries, (item: Country) =>
    deburr(item.Name || '')
  );
  return sortedCountries;
};

export const arePaymentMethodsLoaded =
  (locale: string, amount: number | string) =>
  (state): boolean => {
    if (state.apiPaymentMethods.isError) {
      return true;
    }
    const areMethodsLoaded = getPaymentMethods(state).length !== 0;
    const areMethodsLoading = state.apiPaymentMethods.isLoading;
    const isSameAmount = state.apiPaymentMethods.amount === amount;
    const isSameLocale = state.apiPaymentMethods.locale === locale;
    return (
      (areMethodsLoaded || areMethodsLoading) && isSameAmount && isSameLocale
    );
  };

export const getCompanyName = (state): string | undefined =>
  state.apiCompanies.userCompany.name;
